import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../api/client";

const ACTION_OPTIONS = [
  { value: "", label: "כל הפעולות" },
  { value: "CREATED", label: "נוצר" },
  { value: "UPDATED", label: "עודכן" },
  { value: "DELETED", label: "נמחק" },
  { value: "APPROVED", label: "אושר" },
  { value: "REJECTED", label: "נדחה" },
  { value: "ENDED", label: "הושלם" },
  { value: "DRAW", label: "משיכה" },
  { value: "REPAY", label: "החזר" },
];

const ENTITY_OPTIONS = [
  { value: "", label: "כל הסוגים" },
  { value: "LOAN", label: "הלוואה" },
  { value: "USER", label: "משתמש" },
  { value: "CreditFacility", label: "מסגרת אשראי" },
  { value: "Collateral", label: "בטחון" },
  { value: "CashFlowEntry", label: "תזרים" },
];

const ACTION_LABEL = {
  CREATED: "נוצר", UPDATED: "עודכן", DELETED: "נמחק",
  APPROVED: "אושר", REJECTED: "נדחה", ENDED: "הושלם",
  DRAW: "משיכה", REPAY: "החזר", CREATE: "נוצר", UPDATE: "עודכן", DELETE: "נמחק",
};

const ENTITY_LABEL = {
  LOAN: "הלוואה", USER: "משתמש", CreditFacility: "מסגרת אשראי",
  Collateral: "בטחון", CashFlowEntry: "תזרים",
};

const ACTION_COLOR = {
  CREATED: "var(--success)", CREATE: "var(--success)",
  UPDATED: "var(--accent)", UPDATE: "var(--accent)",
  DELETED: "var(--danger)", DELETE: "var(--danger)",
  APPROVED: "var(--success)",
  REJECTED: "var(--danger)",
  ENDED: "var(--text-secondary)",
  DRAW: "var(--warning, #f59e0b)",
  REPAY: "var(--success)",
};

function formatDate(d) {
  if (!d) return "—";
  const x = new Date(d);
  return x.toLocaleDateString("he-IL") + " " + x.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

function formatChanges(changes) {
  if (!changes || typeof changes !== "object" || Object.keys(changes).length === 0) return null;
  try { return JSON.stringify(changes, null, 2); } catch { return null; }
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Filters — initialise from URL so links and browser back work
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [action, setAction] = useState(searchParams.get("action") || "");
  const [entityType, setEntityType] = useState(searchParams.get("entityType") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("dateTo") || "");
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const LIMIT = 50;

  const debounceRef = useRef(null);

  const fetchLogs = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/auth/activity-logs`, { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync URL + fetch whenever filters change
  useEffect(() => {
    const params = { page, limit: LIMIT };
    if (search) params.search = search;
    if (action) params.action = action;
    if (entityType) params.entityType = entityType;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    // Update URL
    const urlParams = {};
    if (search) urlParams.search = search;
    if (action) urlParams.action = action;
    if (entityType) urlParams.entityType = entityType;
    if (dateFrom) urlParams.dateFrom = dateFrom;
    if (dateTo) urlParams.dateTo = dateTo;
    if (page > 1) urlParams.page = page;
    setSearchParams(urlParams, { replace: true });

    // Debounce only for text search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchLogs(params), search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [search, action, entityType, dateFrom, dateTo, page, fetchLogs, setSearchParams]);

  const resetFilters = () => {
    setSearch(""); setAction(""); setEntityType("");
    setDateFrom(""); setDateTo(""); setPage(1);
  };

  const hasFilters = search || action || entityType || dateFrom || dateTo;

  const handleRowClick = (log) => {
    setExpandedId(expandedId === log._id ? null : log._id);
  };

  const navigateToEntity = (e, log) => {
    e.stopPropagation();
    if (log.entityType === "LOAN" && log.entityId) navigate(`/loan/${log.entityId}`);
    else if (log.entityType === "USER" && log.entityId) navigate(`/users/${log.entityId}`);
  };

  return (
    <div className="page" style={{ width: "100%", boxSizing: "border-box" }}>
      <header style={{ marginBottom: "var(--space-xl)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-md)", marginBottom: "var(--space-xs)" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate("/user-management")}
            style={{ fontSize: 13 }}
          >
            ← ניהול משתמשים
          </button>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>לוג פעילות</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 4 }}>
          {total.toLocaleString()} רשומות סה"כ
        </p>
      </header>

      {/* ── Filters ── */}
      <div style={{
        background: "var(--card-bg)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-lg)",
        marginBottom: "var(--space-lg)",
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--space-md)",
        alignItems: "flex-end",
      }}>
        <div style={{ flex: "1 1 220px" }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>חיפוש חופשי</label>
          <input
            className="input"
            placeholder="שם משתמש, ישות..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ flex: "0 1 160px" }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>פעולה</label>
          <select className="select" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} style={{ width: "100%" }}>
            {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ flex: "0 1 160px" }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>סוג ישות</label>
          <select className="select" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }} style={{ width: "100%" }}>
            {ENTITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ flex: "0 1 145px" }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>מתאריך</label>
          <input type="date" className="input" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} style={{ width: "100%" }} />
        </div>

        <div style={{ flex: "0 1 145px" }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>עד תאריך</label>
          <input type="date" className="input" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} style={{ width: "100%" }} />
        </div>

        {hasFilters && (
          <button type="button" className="btn btn-ghost" onClick={resetFilters} style={{ alignSelf: "flex-end" }}>
            נקה
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{
        background: "var(--card-bg)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
      }}>
        {loading && (
          <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            טוען...
          </div>
        )}

        {!loading && logs.length === 0 && (
          <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            לא נמצאו רשומות
          </div>
        )}

        {!loading && logs.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                <th style={thStyle}>תאריך</th>
                <th style={thStyle}>משתמש</th>
                <th style={thStyle}>פעולה</th>
                <th style={thStyle}>סוג</th>
                <th style={thStyle}>ישות</th>
                <th style={{ ...thStyle, width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const isExpanded = expandedId === log._id;
                const changesText = formatChanges(log.changes);
                const canNavigate = (log.entityType === "LOAN" || log.entityType === "USER") && log.entityId;
                return (
                  <React.Fragment key={log._id}>
                    <tr
                      onClick={() => handleRowClick(log)}
                      style={{
                        borderBottom: isExpanded ? "none" : "1px solid var(--border-light)",
                        cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-subtle)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={tdStyle}>{formatDate(log.createdAt)}</td>
                      <td style={tdStyle}>
                        <span
                          style={{ color: "var(--accent)", cursor: "pointer", textDecoration: "underline" }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/users/${log.userId}`); }}
                        >
                          {log.userName}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 99,
                          fontSize: 11,
                          fontWeight: 600,
                          background: (ACTION_COLOR[log.action] || "var(--text-secondary)") + "22",
                          color: ACTION_COLOR[log.action] || "var(--text-secondary)",
                        }}>
                          {ACTION_LABEL[log.action] || log.action}
                        </span>
                      </td>
                      <td style={tdStyle}>{ENTITY_LABEL[log.entityType] || log.entityType}</td>
                      <td style={tdStyle}>
                        {canNavigate ? (
                          <span
                            style={{ color: "var(--accent)", cursor: "pointer", textDecoration: "underline" }}
                            onClick={(e) => navigateToEntity(e, log)}
                          >
                            {log.entitySummary || log.entityId}
                          </span>
                        ) : (
                          log.entitySummary || log.entityId || "—"
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                        {changesText ? (isExpanded ? "▲" : "▼") : ""}
                      </td>
                    </tr>
                    {isExpanded && changesText && (
                      <tr style={{ borderBottom: "1px solid var(--border-light)" }}>
                        <td colSpan={6} style={{ padding: "0 var(--space-lg) var(--space-md)", background: "var(--bg-subtle)" }}>
                          <pre style={{
                            margin: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all",
                            color: "var(--text-secondary)", fontFamily: "var(--font-mono, monospace)",
                            background: "var(--bg)", borderRadius: "var(--radius)", padding: "var(--space-sm)",
                          }}>
                            {changesText}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "var(--space-sm)", marginTop: "var(--space-lg)" }}>
          <button className="btn btn-ghost" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="btn btn-ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
          <span style={{ fontSize: 13, color: "var(--text-secondary)", minWidth: 80, textAlign: "center" }}>
            עמוד {page} מתוך {pages}
          </span>
          <button className="btn btn-ghost" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}>›</button>
          <button className="btn btn-ghost" onClick={() => setPage(pages)} disabled={page === pages}>»</button>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: "10px 14px",
  textAlign: "right",
  fontWeight: 600,
  fontSize: 12,
  color: "var(--text-secondary)",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "10px 14px",
  verticalAlign: "middle",
};
